import { Box, ButtonBase, Stack } from "@mui/material";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { GoogleMapErrorBoundary } from "../../GeoLocation/GoogleMapErrorBoundary";
import { useOpenGeoLocation } from "../../OpenGeoLocation/useOpenGeoLocation";

interface PlacementLocationMapProps {
  coordinates: number[];
  formattedAddress: string;
  placementId: string;
  placementCandidateId?: string;
}

export function PlacementLocationMap(props: PlacementLocationMapProps) {
  const { coordinates, formattedAddress, placementId, placementCandidateId } = props;
  const worker = useDefinedWorker();

  const { googleMapsHref } = useOpenGeoLocation({
    location: {
      lat: coordinates[1],
      lng: coordinates[0],
    },
    formattedAddress,
  });

  return (
    <Box sx={{ position: "relative" }}>
      <GoogleMapErrorBoundary>
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "250px",
            borderRadius: "16",
          }}
          center={{
            lat: coordinates[1],
            lng: coordinates[0],
          }}
          zoom={15}
          options={{
            disableDefaultUI: true,
            gestureHandling: "cooperative",
          }}
        >
          <Marker
            position={{
              lat: coordinates[1],
              lng: coordinates[0],
            }}
          />
        </GoogleMap>
      </GoogleMapErrorBoundary>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          position: "absolute",
          bottom: "16px",
          left: "16px",
          right: "16px",
          padding: "16px",
          backgroundColor: (theme) => theme.palette.background.tertiary,
          borderRadius: 4,
          border: "0.5px solid",
          borderColor: (theme) => theme.palette.border?.primary,
        }}
      >
        <Text variant="body2" color="text.secondary">
          {formattedAddress}
        </Text>
        <ButtonBase
          onClick={() => {
            logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
              action: "open_in_maps",
              placementId,
              placementCandidateId,
              source: placementCandidateId ? "jobs" : "bookings",
              workerId: worker.userId,
            });

            window.open(googleMapsHref, "_blank");
          }}
        >
          <Text variant="body1" sx={{ minWidth: "64px" }}>
            Open in map
          </Text>
          <ArrowRightIcon />
        </ButtonBase>
      </Stack>
    </Box>
  );
}
